import { CHAT_API_BASE_URL, chatApiRuProxy } from '../../constants/chat';

/**
 * Единственная дверь из браузера в ИИ-сервис (`awesome-chat-api`).
 *
 * ## Зачем
 *
 * Отчёт 29.09: «из РФ есть проблемы с доступом к ИИ-чату на проде». Разбор
 * показал две вещи, и вторая неожиданная.
 *
 * **Срока не было ни у одного вызова.** Пять модулей из шести звали
 * `fetch` голым: знакомство (`/onboarding/start`), пределы вложений,
 * модерация голоса, проактивность, продуктовые события. На формируемом РФ-
 * трафике такой вызов не падает, а ВИСИТ — и `catch` с запасными значениями
 * рядом не срабатывает никогда, потому что зависание не бросает исключение.
 * Этот же класс в репозитории ловили трижды: CORE-119, CORE-806, CORE-1356.
 *
 * **Запасной РФ-путь существовал и не использовался.** У хаба он есть с
 * CORE-407, у ИИ-сервиса не было — при том что релей на московском эдже давно
 * поднят и отдаёт ТЕ ЖЕ ручки. Проверено на всех трёх стендах; на проде
 * `chat-ru.agapella.io` возвращает байт в байт то же, что `chat-api`. Дорога
 * была построена, а клиент о ней не знал.
 *
 * ## Что делает эта дверь
 *
 * Ставит срок, а по его истечении (или на сетевом отказе) повторяет запрос
 * через релей. Путь, который сработал, запоминается на сессию: платить
 * секундами ожидания за каждый следующий вызов не нужно — ровно тот же приём,
 * что у хаба (`markHubPathStalled`).
 *
 * ## Чего она не делает
 *
 * Не глушит ошибки. Отказ сервера — это ответ, и он возвращается как есть:
 * лечить четырёхсотку сменой дороги бессмысленно, а молча подменять её
 * запасным значением — прятать дефект.
 *
 * Не повторяет успевший ответить запрос. Релей включается только там, где
 * прямой путь МОЛЧИТ, и только один раз.
 */

/**
 * Сколько ждать прямой путь, прежде чем пробовать релей.
 *
 * Шесть секунд: обычный ответ ИИ-сервиса — доли секунды (замер 29.09:
 * 0,2–0,5 с на всех трёх стендах), так что шесть — это заведомо «молчит», а не
 * «медленно». Больше значило бы держать человека перед пустым экраном дольше,
 * чем нужно, чтобы понять: эта дорога не работает.
 */
export const CHAT_API_DEADLINE_MS = 6_000;

/** Путь, который в этой сессии уже сработал. Пусто — ещё не выяснили. */
let workingBase: string | null = null;

/** Для тестов: забыть выученный путь. */
export function resetChatApiPath(): void {
  workingBase = null;
}

/** Какой путь сейчас считается рабочим. Нужно диагностике и тестам. */
export function currentChatApiBase(): string | null {
  return workingBase;
}

async function once(base: string, path: string, init: RequestInit, ms: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(`${base}${path}`, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Запрос к ИИ-сервису со сроком и запасным РФ-путём.
 *
 * `path` — с ведущим слэшем, например `/api/v2/attachment-limits`.
 */
export async function chatApiFetch(
  path: string,
  init: RequestInit = {},
  deadlineMs: number = CHAT_API_DEADLINE_MS,
): Promise<Response> {
  const direct = CHAT_API_BASE_URL.replace(/\/+$/, '');
  const relay = chatApiRuProxy().replace(/\/+$/, '');

  // Выученный путь идёт первым: со второго вызова человек не ждёт впустую.
  const first = workingBase ?? direct;
  try {
    const res = await once(first, path, init, deadlineMs);
    workingBase = first;
    return res;
  } catch (err) {
    // Отказ сервера сюда не попадает — `fetch` отдаёт его ответом, а не
    // броском. Здесь только молчание: срок вышел или сети нет.
    const second = first === direct ? relay : direct;
    if (!second) throw err;
    const res = await once(second, path, init, deadlineMs);
    workingBase = second;
    return res;
  }
}
